import { useEffect, useState } from "react";

import { PCRS_STATUS } from "features/cdm/constants/pcrsSchemeCheck";
import { useCdmPatientDetails } from "features/cdm/hooks/useCdmPatientDetails";
import { useStoredPcrsCredentials } from "features/cdm/hooks/useStoredPcrsCredentials";
import { warmUpPcrsCertificate } from "features/pcrsExtension";

const PCRS_CONNECTION_ERROR_MESSAGE =
  "Could not connect to PCRS. Please recheck your certificate, username and password, then try again.";

// Owns the PCRS credential prompt + CDM patient-details (cdm-ws) connection:
// warms the client-certificate picker on entry, prompts for credentials once the
// scheme check passes and nothing is stored, fetches the patient details, and
// re-prompts (with guidance) on a connection error. Returns the details, the
// derived web-service connection flag, and ready-to-spread credentials-dialog
// props for PcrsCredentialsDialog.
export const usePcrsConnection = ({ patientId, schemeCheck }) => {
  const isPatientValid = !!schemeCheck?.validPatient;
  // A Private patient has no GMS entitlement: the scheme check is skipped and no
  // cdm-ws / credential PCRS calls are made. PCRS is treated as N/A (not an
  // error) so CDM actions are never blocked by a missing web-service connection.
  const isPrivate = schemeCheck?.status === PCRS_STATUS.PRIVATE_SKIPPED;

  const {
    username,
    password,
    hasCredentials,
    isLoaded: areCredentialsLoaded,
    saveCredentials,
  } = useStoredPcrsCredentials();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  // Bumped on each save so the details query refetches with the new values
  // (avoids a stale-closure refetch).
  const [credentialsVersion, setCredentialsVersion] = useState(0);

  // Surface the browser's client-certificate picker on entry so the extension's
  // later mTLS calls can reuse the cached choice instead of failing silently.
  useEffect(() => {
    warmUpPcrsCertificate();
  }, []);

  useEffect(() => {
    if (isPatientValid && !isPrivate && areCredentialsLoaded && !hasCredentials) {
      setError(null);
      setIsDialogOpen(true);
    }
  }, [isPatientValid, isPrivate, areCredentialsLoaded, hasCredentials]);

  const {
    data: cdmPatientDetails,
    isError: isCdmPatientDetailsError,
    isFetching: isCdmPatientDetailsFetching,
  } = useCdmPatientDetails({
    patientId,
    cardType: "GMS",
    username,
    password,
    credentialsVersion,
    enabled: isPatientValid && hasCredentials && !isPrivate,
  });

  useEffect(() => {
    if (isCdmPatientDetailsError && !isCdmPatientDetailsFetching) {
      setError(PCRS_CONNECTION_ERROR_MESSAGE);
      setIsDialogOpen(true);
    }
  }, [isCdmPatientDetailsError, isCdmPatientDetailsFetching]);

  // The cdm-ws details lag behind the scheme check, so consumers must know when
  // they are still in flight — otherwise the PCRS dialog / warning calculations
  // run against not-yet-returned data and briefly show "details not returned".
  // Private patients skip the details call, so they are never "loading".
  const isCdmPatientDetailsLoading =
    !isPrivate &&
    isCdmPatientDetailsFetching &&
    !cdmPatientDetails &&
    !isCdmPatientDetailsError;

  // Whether the cdm-ws details are genuinely expected but not yet resolved.
  // Unlike the fetching-based flag above this stays true across the brief gap
  // between "the query becomes enabled" and "the request is in flight" (and
  // while the credential store is still loading), so callers that gate on it
  // (content skeleton, BE sync) never post/render prematurely. It resolves the
  // moment we have details or an error — and is never true for private / no-GMS
  // / no-credentials patients — so it can never hang.
  const isDetailsExpected =
    isPatientValid && !isPrivate && (hasCredentials || !areCredentialsLoaded);
  const isCdmPatientDetailsPending =
    isDetailsExpected && !cdmPatientDetails && !isCdmPatientDetailsError;

  const handleSaveCredentials = async (values) => {
    await saveCredentials(values);
    setError(null);
    setCredentialsVersion((version) => version + 1);
  };

  return {
    cdmPatientDetails,
    isCdmPatientDetailsError,
    isCdmPatientDetailsLoading,
    isCdmPatientDetailsPending,
    // Private patients never need the cdm-ws connection, so PCRS is N/A rather
    // than a blocking error — treat the web service as "connected" for them.
    hasWsConnection: isPrivate || (!!cdmPatientDetails && !isCdmPatientDetailsError),
    credentialsDialog: {
      open: isDialogOpen,
      onClose: () => setIsDialogOpen(false),
      onSave: handleSaveCredentials,
      defaultUsername: username,
      errorMessage: error,
    },
  };
};
